<script setup lang="ts">
    import { computed } from 'vue';
    import type { Issue } from '../../interfaces';
    import { IssueStatus, TrackingOptions } from '../../interfaces';
    import { CommunicationType } from '../../../Event/EventTypes';
    import ShareIcons from '@shared/components/ShareIcons.vue';
    import TagsList from '@shared/components/Tags/TagsList.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import IssueSubject from '@cwd/Views/Issues/IssueSubject.vue';
    import IssueDynamicSubject from '@cwd/Views/Issues/IssueDynamicSubject.vue';
    import InfoPopoverButton from '@shared/components/Form/InfoPopoverButton.vue';
    import type { DynamicSubjectRow } from '@cwd/Views/Issues/dynamicSubject';

    const { issue, communicationType } = defineProps<{
        issue: Issue;
        sender: string | null;
        hasIssueVariations: boolean;
        communicationType: CommunicationType | null;
        onSubjectClick?: (rows: DynamicSubjectRow[], negate: boolean, label: string) => void;
    }>();

    // Programmatic event communications (Reminder/Accepted/Participated/Waitlist) have no editable
    // recipient list/segments - show the communication type instead, matching IssueEditorViewModel.js.
    const hasCommunicationTypeNotCustom = computed(() => {
        if (!issue.Event?.EventId || !communicationType) return false;
        return communicationType !== CommunicationType.Custom;
    });

    const communicationTypeLabel = computed(() => {
        switch (communicationType) {
            case CommunicationType.Custom:
                return $Resources.CommunicationTypeCustom;
            case CommunicationType.Reminder:
                return $Resources.CommunicationTypeReminder;
            case CommunicationType.Accepted:
                return $Resources.CommunicationTypeAccepted;
            case CommunicationType.Participated:
                return $Resources.CommunicationTypeParticipated;
            case CommunicationType.Waitlist:
                return $Resources.CommunicationTypeWaitlist;
            default:
                return '';
        }
    });
</script>

<template>
    <div class="list-header">
        <IssueDynamicSubject
            v-if="issue.DynamicSubject"
            :dynamic-subject="issue.DynamicSubject"
            :categoryName="issue.Category.Name"
            :onSubjectClick="onSubjectClick" />

        <h1 v-else>
            <IssueSubject :subject="issue.Subject" :event-name="issue.Event?.Name" />
            <small class="m-sm-l">{{ issue.Category.Name }}</small>
        </h1>

        <p v-if="issue.IssueName">{{ issue.IssueName }}</p>

        <p v-if="issue.Journey">
            <span class="fw-600 m-xs-r">{{ $Resources.AssociatedJourneyLabel }}:</span>
            <a :href="`/Journeys/${issue.Journey.JourneyId}`" target="_blank" rel="noopener">{{ issue.Journey.Title }}</a>
        </p>

        <p v-if="issue.Event">
            <span class="fw-600 m-xs-r">{{ $Resources.EventLabel }}:</span>
            <a :href="`/Events/${issue.Event.EventId}`" target="_blank" rel="noopener">{{ issue.Event.Name }}</a>
        </p>

        <p v-if="issue.Survey">
            <span class="fw-600 m-xs-r">{{ $Resources.IssueStatisticsSurveyLabel }}:</span>
            <a :href="`/Surveys/${issue.Survey.SurveyId}`" target="_blank" rel="noopener">{{ issue.Survey.Title }}</a>
        </p>

        <ShareIcons :id="issue.IssueId" />

        <div v-if="hasIssueVariations">
            <p class="m-xs-b">{{ $Resources.AbTestSubjectResultHeading }}:</p>

            <div v-for="(v, i) in issue.IssueVariationConfiguration?.IssueVariations" :key="v.IssueVariationId">
                <h2 class="h3">
                    <span class="c-light">{{ String.fromCharCode('A'.charCodeAt(0) + i) }}:</span>
                    {{ v.AlternativeSubject }}
                    <span v-if="v.AlternativeSubject === issue.Subject" class="mini-label mini-label-default m-xs-l">
                        {{ $Resources.AbTestSubjectResultDefaultSubject }}
                    </span>
                    <span v-if="v.IssueVariationId === issue.IssueVariationConfiguration!.WinnerId" class="mini-label mini-label-success m-xs-l">
                        {{ $Resources.AbTestSubjectResultWinnerSubject }}
                    </span>
                </h2>
            </div>

            <BsAlert class="m-sm-b" :show="!!issue.IssueVariationConfiguration?.WinnerId" :type="AlertTypes.Info" :dismissible="false">
                <template #content>
                    {{ $Resources.AbTestSubjectResultTextPart1 }}
                    <strong>{{ issue.IssueVariationConfiguration?.PercentToTest }}%</strong>
                    {{ $Resources.AbTestSubjectResultTextPart2 }}
                    <strong>{{ issue.IssueVariationConfiguration?.WaitTime }} {{ $Resources.AbTestSubjectResultTextPart3 }}</strong>
                    {{ $Resources.AbTestSubjectResultTextPart4 }}
                    <strong v-if="issue.IssueVariationConfiguration?.WinType === 0">{{ $Resources.AbTestSubjectResultTextPart5 }}</strong>
                    <strong v-if="issue.IssueVariationConfiguration?.WinType === 1">{{ $Resources.AbTestSubjectResultTextPart6 }}</strong>
                    .
                </template>
            </BsAlert>

            <BsAlert
                class="m-sm-b"
                :show="!issue.IssueVariationConfiguration?.WinnerId && issue.Status !== IssueStatus.Sent"
                :type="AlertTypes.Info"
                :dismissible="false"
                :content="$Resources.AbTestEventSplitStatisticsDescription" />
        </div>

        <p>
            <span class="fw-600 m-xs-r">{{ $Resources.FromLabel }}:</span>
            {{ issue.FromName }} [{{ issue.ReplyToAddress }}]
        </p>

        <p v-if="hasCommunicationTypeNotCustom">
            <span class="fw-600 m-xs-r">{{ $Resources.RecipientsLabel }}:</span>
            {{ communicationTypeLabel }}
        </p>

        <ul v-else class="list-inline list-unstyled">
            <li>
                <span class="fw-600 m-xs-r">{{ $Resources.RecipientsLabel }}:</span>
                <InfoPopoverButton :heading="$Resources.RecipientsLabel" :info-text="$Resources.RecipientsForMailingInfoText" direction="right" />
            </li>
            <li v-for="seg in issue.Segments" :key="seg.SegmentId">
                <a :href="`/Segments/${seg.SegmentId}`" target="_blank" rel="noopener">{{ seg.Name }}</a>
                <span class="mini-label mini-label-info m-xs-l">{{ $Resources.SegmentLabel }}</span>
            </li>
            <li v-for="list in issue.Lists" :key="list.ListId">
                <a :href="`/Lists/${list.ListId}`" target="_blank" rel="noopener">{{ list.Name }}</a>
                <span class="mini-label mini-label-info m-xs-l">{{ $Resources.ListLabel }}</span>
            </li>
        </ul>

        <p v-if="issue.TrackingOptions !== undefined">
            <span class="fw-600 m-xs-r">{{ $Resources.IssueStatisticsTrackingOptions }}:</span>
            <span v-if="issue.TrackingOptions === TrackingOptions.StandardTracking">{{ $Resources.StandardTrackingLabel }}</span>
            <span v-else-if="issue.TrackingOptions === TrackingOptions.UTMTracking">{{ $Resources.UTMTrackingLabel }}</span>
            <span v-else-if="issue.TrackingOptions === TrackingOptions.UngappedIdTracking">{{ $Resources.UngappedIdTrackingLabel }}</span>
            <span v-else-if="issue.TrackingOptions === TrackingOptions.UTMAndUngappedIdTracking">{{ $Resources.UTMAndUngappedIdTrackingLabel }}</span>
            <span v-else-if="issue.TrackingOptions === TrackingOptions.Anonymous">{{ $Resources.AnonymTrackingLabel }}</span>
            <span v-else-if="issue.TrackingOptions === TrackingOptions.NoTracking">{{ $Resources.NoTrackingLabel }}</span>
            <span v-else-if="issue.TrackingOptions === TrackingOptions.UTMAndAnonymous">{{ $Resources.UTMAndAnonymousTrackingLabel }}</span>
        </p>

        <TagsList v-if="issue.Tags?.length" :tags="issue.Tags" readonly showHeading />

        <p class="meta">
            <template v-if="issue.StartedDateTime ?? issue.Started">
                <span class="glyphicon glyphicon-time"></span>
                {{ $Resources.IssueStatisticsMailSent }}
                {{ new Date(issue.StartedDateTime ?? issue.Started!).toLocaleString() }}
            </template>
            <template v-if="sender">
                <span class="glyphicon glyphicon-user"></span>
                {{ $Resources.IssueStatisticsSentBy }} {{ sender }}
            </template>
            <template v-if="issue.LastModifiedBy">
                <span class="glyphicon glyphicon-user"></span>
                {{ $Resources.IssueStatisticsModifiedBy }} {{ issue.LastModifiedBy.Name }}
            </template>
            <template v-if="issue.LastModified">
                <span class="glyphicon glyphicon-edit"></span>
                {{ $Resources.IssueStatisticsModified }}
                {{ new Date(issue.LastModified).toLocaleString() }}
            </template>
        </p>

        <BsAlert
            :show="issue.TrackingOptions === TrackingOptions.Anonymous || issue.TrackingOptions === TrackingOptions.UTMAndAnonymous"
            :type="AlertTypes.Warning"
            :dismissible="false"
            :content="$Resources.IssueStatisticsAnonymousWarning" />
    </div>
</template>
